import { Checkbox } from '@creditornot/cb-checkbox';
import { cssThemedValue, colors } from '@creditornot/cb-ingredients';
import React, { useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { useResizeObserver } from 'common/hooks';
import { Typography as T } from 'common/typography';
import LazyLoadedThumbnail from 'modules/menu/components/LazyLoadedThumbnail';
import EmptyThumbnail from 'modules/menu/items/components/EmptyThumbnail';
import { ItemQuantity } from 'modules/menu/items/types';
import { solidify } from 'modules/menu/solidify';
import { SelectionCategory } from 'modules/shared-menu/types';

import { OfferPrice, WeightBasedItemAvailableQuantity } from './OfferHeader';
import {
  Header,
  Left,
  ThumbnailContainer,
  NameAndDescription,
  NameAndTags,
  Name,
  StyledTags,
  Description,
} from './styles';
import { MENU_MOBILE_BREAKPOINT, WOLT_SPECIALS_CATEGORY_ID } from '../../constants';
import { Offer } from '../../types';

type Props = {
  category: SelectionCategory | null;
  className?: string;
  item: Offer;
  selected: boolean;
  primaryLanguage: Language;
  itemInventoryCount?: ItemQuantity;
};

const Root = styled.div`
  min-width: 550px;
  background-color: ${cssThemedValue({
    light: solidify(colors.salt100, 'light'),
    dark: solidify('#141414', 'dark'),
  })} !important;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    min-width: 320px;
  }
`;

const StyledHeader = styled(Header)`
  background-color: ${({ selected }) =>
    selected ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
`;

const StyledCheckbox = styled(React.memo(Checkbox))`
  margin-inline-start: 8px;
  margin-inline-end: 16px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

const Quantity = styled(T.Body3)`
  color: ${cssThemedValue('textSecondary')};
  margin-inline-start: 12px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

const DragOverlayOfferHeader = ({
  className,
  category,
  item,
  selected,
  primaryLanguage,
  itemInventoryCount,
}: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);

  const name = useMemo(
    () => item.name.find(t => t.lang === primaryLanguage) || item.name[0],
    [item.name, primaryLanguage],
  );
  const description = useMemo(
    () => item.description?.find(t => t.lang === primaryLanguage),
    [item.description, primaryLanguage],
  );

  const { width } = useResizeObserver(rootRef);

  /* 24px account for the left navigation when collapsed */
  const isNarrowView = useMemo(() => width !== undefined && width <= MENU_MOBILE_BREAKPOINT - 24, [width]);

  const isBinaryInventory = useMemo(
    () => !item.inventory_mode || item.inventory_mode === 'forced_out_of_stock',
    [item.inventory_mode],
  );

  return (
    <Root className={className} ref={rootRef}>
      <StyledHeader selected={selected}>
        <Left>
          <StyledCheckbox checked={selected} readOnly />
          <ThumbnailContainer>
            {item.image ? (
              <LazyLoadedThumbnail alt={name.value} blurhash={item.image_blurhash ?? null} src={item.image} />
            ) : (
              <EmptyThumbnail />
            )}
          </ThumbnailContainer>
          <NameAndDescription>
            <NameAndTags>
              <Name dir="auto" lang={name.lang}>
                {name.value}
              </Name>
              <StyledTags
                enabledConfig={item.enabled}
                soldOut={item.inventory_mode === 'forced_out_of_stock'}
                primaryLanguage={primaryLanguage}
                purchasabilityErrors={[]}
                special={category?.id === WOLT_SPECIALS_CATEGORY_ID}
                isTiny={isNarrowView}
              />
              {!isBinaryInventory && itemInventoryCount && (
                <Quantity>
                  {!item.sell_by_weight_config && (
                    <>
                      {itemInventoryCount.available}/{itemInventoryCount.balance}{' '}
                      <FormattedMessage id="menu-item.available" />
                    </>
                  )}
                  {item.sell_by_weight_config && item.sell_by_weight_config.input_type === 'grams' && (
                    <>
                      <WeightBasedItemAvailableQuantity itemInventoryCount={itemInventoryCount} />{' '}
                      <FormattedMessage id="menu-item.available" />
                    </>
                  )}
                  {item.sell_by_weight_config && item.sell_by_weight_config?.input_type !== 'grams' && (
                    <>
                      {itemInventoryCount.available}/{itemInventoryCount.balance}{' '}
                      <FormattedMessage id="menu-item.available" />
                    </>
                  )}
                </Quantity>
              )}
              {isNarrowView && <OfferPrice offer={item} />}
            </NameAndTags>
            {!isNarrowView && description && (
              <Description dir="auto" lang={description.lang}>
                {description.value}
              </Description>
            )}
          </NameAndDescription>
        </Left>
      </StyledHeader>
    </Root>
  );
};

export default React.memo(DragOverlayOfferHeader);
